import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { hexToRgba, rgbaToHex } from '../../core/color';
import {
  gradientToCss,
  normaliseGradient,
  reverseGradient,
  sampleGradient,
  type ColorStop,
  type Gradient,
  type OpacityStop,
} from '../../core/gradient';
import { clamp } from '../../core/math';
import { Modal, NumberInput } from '../components/controls';

export interface GradientEditorProps {
  gradient: Gradient;
  presets: Gradient[];
  onChange: (gradient: Gradient) => void;
  onClose: () => void;
}

/** Checkerboard shown behind translucent stretches of the gradient. */
const CHECKER_LAYER = 'repeating-conic-gradient(var(--border) 0% 25%, var(--bg-input) 0% 50%)';

function overCheckerboard(css: string): { backgroundImage: string; backgroundSize: string } {
  return {
    backgroundImage: `linear-gradient(to right, ${css}), ${CHECKER_LAYER}`,
    backgroundSize: '100% 100%, 12px 12px',
  };
}

function sortByPosition<T extends { position: number }>(stops: readonly T[]): T[] {
  return [...stops].sort((a, b) => a.position - b.position);
}

type Selection = { kind: 'color' | 'opacity'; index: number };

interface DragState {
  kind: 'color' | 'opacity';
  index: number;
  rowLeft: number;
  rowWidth: number;
}

export function GradientEditor({
  gradient,
  presets,
  onChange,
  onClose,
}: GradientEditorProps): JSX.Element {
  const [selected, setSelected] = useState<Selection | null>(null);
  const dragRef = useRef<DragState | null>(null);

  const colorStops = sortByPosition(gradient.stops);
  const opacityStops = sortByPosition(gradient.opacityStops);

  const emit = (next: Gradient) => onChange(normaliseGradient(next));

  const positionFromPointer = (event: React.PointerEvent, rowLeft: number, rowWidth: number) => {
    const fraction = rowWidth > 0 ? (event.clientX - rowLeft) / rowWidth : 0;
    return clamp(fraction, 0, 1);
  };

  const updateColorStop = (index: number, patch: Partial<ColorStop>) => {
    const stops = colorStops.map((stop, i) => (i === index ? { ...stop, ...patch } : stop));
    emit({ ...gradient, stops });
  };

  const updateOpacityStop = (index: number, patch: Partial<OpacityStop>) => {
    const stops = opacityStops.map((stop, i) => (i === index ? { ...stop, ...patch } : stop));
    emit({ ...gradient, opacityStops: stops });
  };

  const handleRowPointerDown = (
    event: React.PointerEvent<HTMLDivElement>,
    kind: 'color' | 'opacity',
  ) => {
    if (event.target !== event.currentTarget) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const position = positionFromPointer(event, rect.left, rect.width);
    const sample = sampleGradient(gradient, position);
    if (kind === 'color') {
      const stops = [...colorStops, { position, color: sample }];
      emit({ ...gradient, stops });
      setSelected({ kind, index: sortByPosition(stops).findIndex((s) => s.position === position) });
    } else {
      const stops = [...opacityStops, { position, opacity: sample.a }];
      emit({ ...gradient, opacityStops: stops });
      setSelected({ kind, index: sortByPosition(stops).findIndex((s) => s.position === position) });
    }
  };

  const handleStopPointerDown = (
    event: React.PointerEvent<HTMLButtonElement>,
    kind: 'color' | 'opacity',
    index: number,
  ) => {
    event.stopPropagation();
    setSelected({ kind, index });
    const rect = event.currentTarget.parentElement?.getBoundingClientRect();
    dragRef.current = { kind, index, rowLeft: rect?.left ?? 0, rowWidth: rect?.width ?? 1 };
    // Capture keeps the drag alive when the pointer leaves the narrow stop row;
    // it can legitimately fail for synthetic or already-released pointers.
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* not fatal */
    }
  };

  const handleStopPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const position = positionFromPointer(event, drag.rowLeft, drag.rowWidth);
    if (drag.kind === 'color') updateColorStop(drag.index, { position });
    else updateOpacityStop(drag.index, { position });
  };

  const handleStopPointerUp = () => {
    dragRef.current = null;
  };

  const handleDelete = () => {
    if (!selected) return;
    if (selected.kind === 'color') {
      if (colorStops.length <= 2) return;
      emit({ ...gradient, stops: colorStops.filter((_, i) => i !== selected.index) });
    } else {
      if (opacityStops.length <= 2) return;
      emit({ ...gradient, opacityStops: opacityStops.filter((_, i) => i !== selected.index) });
    }
    setSelected(null);
  };

  const handleReverse = () => {
    emit(reverseGradient(gradient));
    setSelected(null);
  };

  const deleteDisabled =
    !selected || (selected.kind === 'color' ? colorStops.length <= 2 : opacityStops.length <= 2);

  const selectedColorStop = selected?.kind === 'color' ? colorStops[selected.index] : undefined;
  const selectedOpacityStop =
    selected?.kind === 'opacity' ? opacityStops[selected.index] : undefined;

  return (
    <Modal
      open
      title="Gradient Editor"
      onClose={onClose}
      footer={
        <button type="button" className="button button--primary" onClick={onClose}>
          OK
        </button>
      }
    >
      <div className="gradient-editor">
        <div className="gradient-editor__presets">
          {presets.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className="gradient-editor__preset"
              title={preset.name}
              style={overCheckerboard(gradientToCss(preset))}
              onClick={() => emit(preset)}
            />
          ))}
        </div>

        <div className="gradient-editor__preview" style={overCheckerboard(gradientToCss(gradient))} />

        <div className="gradient-editor__stops">
          <div
            className="gradient-editor__row gradient-editor__row--opacity"
            onPointerDown={(event) => handleRowPointerDown(event, 'opacity')}
          >
            {opacityStops.map((stop, index) => (
              <button
                key={index}
                type="button"
                className={`gradient-editor__stop gradient-editor__stop--opacity${
                  selected?.kind === 'opacity' && selected.index === index
                    ? ' gradient-editor__stop--selected'
                    : ''
                }`}
                style={{ left: `${stop.position * 100}%`, opacity: stop.opacity }}
                onPointerDown={(event) => handleStopPointerDown(event, 'opacity', index)}
                onPointerMove={handleStopPointerMove}
                onPointerUp={handleStopPointerUp}
                onPointerCancel={handleStopPointerUp}
              />
            ))}
          </div>
          <div
            className="gradient-editor__row gradient-editor__row--color"
            onPointerDown={(event) => handleRowPointerDown(event, 'color')}
          >
            {colorStops.map((stop, index) => (
              <button
                key={index}
                type="button"
                className={`gradient-editor__stop gradient-editor__stop--color${
                  selected?.kind === 'color' && selected.index === index
                    ? ' gradient-editor__stop--selected'
                    : ''
                }`}
                style={{ left: `${stop.position * 100}%`, backgroundColor: rgbaToHex(stop.color) }}
                onPointerDown={(event) => handleStopPointerDown(event, 'color', index)}
                onPointerMove={handleStopPointerMove}
                onPointerUp={handleStopPointerUp}
                onPointerCancel={handleStopPointerUp}
              />
            ))}
          </div>
        </div>

        <div className="gradient-editor__toolbar">
          <button type="button" className="button" onClick={handleReverse}>
            Reverse
          </button>
          <button
            type="button"
            className="button button--danger"
            disabled={deleteDisabled}
            onClick={handleDelete}
          >
            Delete Stop
          </button>
        </div>

        {selectedColorStop && selected && (
          <div className="gradient-editor__inspector">
            <div className="field">
              <span className="field__label">Color</span>
              <input
                className="gradient-editor__color-input"
                type="color"
                value={rgbaToHex(selectedColorStop.color)}
                onChange={(event) =>
                  updateColorStop(selected.index, {
                    color: hexToRgba(event.target.value, selectedColorStop.color.a),
                  })
                }
              />
            </div>
            <div className="field">
              <span className="field__label">Location</span>
              <NumberInput
                value={Math.round(selectedColorStop.position * 100)}
                min={0}
                max={100}
                width={56}
                onChange={(value) => updateColorStop(selected.index, { position: value / 100 })}
              />
              <span className="field__label">%</span>
            </div>
          </div>
        )}

        {selectedOpacityStop && selected && (
          <div className="gradient-editor__inspector">
            <div className="field">
              <span className="field__label">Opacity</span>
              <NumberInput
                value={Math.round(selectedOpacityStop.opacity * 100)}
                min={0}
                max={100}
                width={56}
                onChange={(value) => updateOpacityStop(selected.index, { opacity: value / 100 })}
              />
              <span className="field__label">%</span>
            </div>
            <div className="field">
              <span className="field__label">Location</span>
              <NumberInput
                value={Math.round(selectedOpacityStop.position * 100)}
                min={0}
                max={100}
                width={56}
                onChange={(value) => updateOpacityStop(selected.index, { position: value / 100 })}
              />
              <span className="field__label">%</span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
